// BL-177 Step 2l: Company inbox / My inbox, conversation privacy, join requests and invites.
// The rules live in the database (migration 212_bl177_step2l_inboxes_privacy_collab: restrictive row-level security on the
// conversations and on every table that exposes them). This file only shapes what the screen shows and calls the database
// functions; nothing here can widen what a person is allowed to see.

export const ACCESS_NOTE_MAX = 200;

export const PRIVACY_LEVELS = [
    { value: 'busy', label: 'Busy only', help: 'Colleagues see "<your name> is in a conversation" and nothing else. No client, no channel, and nobody can ask to join.' },
    { value: 'limited', label: 'Limited', help: 'Colleagues see the client name and the channel, and can ask to join. This is the default.' },
    { value: 'full', label: 'Full', help: 'Colleagues see the client name, the channel and the conversation itself (read-only), and can ask to join.' },
];
export const DEFAULT_PRIVACY_LEVEL = 'limited';

export function normalizePrivacyLevel(value) {
    return PRIVACY_LEVELS.some((level) => level.value === value) ? value : DEFAULT_PRIVACY_LEVEL;
}

/** The note on a join request / invite / pass: required, one line, at most 200 characters. */
export function validateAccessNote(value) {
    const note = String(value ?? '').trim();
    if (!note) return { ok: false, note, error: 'Say in one line why.' };
    if (/[\r\n]/.test(note)) return { ok: false, note, error: 'Keep it to one line.' };
    if (note.length > ACCESS_NOTE_MAX) return { ok: false, note, error: `At most ${ACCESS_NOTE_MAX} characters (${note.length} now).` };
    return { ok: true, note, error: '' };
}

/** What the input may hold: the first line only, cut at 200 characters. */
export function clampAccessNote(value) {
    return String(value ?? '').split(/[\r\n]/)[0].slice(0, ACCESS_NOTE_MAX);
}

/**
 * Which inbox is each conversation in?
 *   mine       a participant row for me
 *   colleague  somebody else has it (the database tells us through the colleague cards; a conversation we can read is not
 *              necessarily the Company inbox: "full" privacy makes it readable)
 *   company    nobody has it
 */
export function inboxOf(sessionId, { mineIds, colleagueIds }) {
    if (mineIds?.has(sessionId)) return 'mine';
    if (colleagueIds?.has(sessionId)) return 'colleague';
    return 'company';
}

export function splitByInbox(threads = [], { mineIds = new Set(), colleagueIds = new Set() } = {}) {
    const out = { company: [], mine: [] };
    threads.forEach((thread) => {
        const where = inboxOf(thread.id, { mineIds, colleagueIds });
        if (where === 'mine') out.mine.push(thread);
        else if (where === 'company') out.company.push(thread);
    });
    return out;
}

/** The thin band: only while more than one staff member is in the conversation. */
export function othersBandText(participantCount) {
    const count = Number(participantCount) || 0;
    return count > 1 ? `Others are in this conversation (${count})` : '';
}

export function joinedAlertText(name) {
    return `${String(name || 'A colleague').trim() || 'A colleague'} joined`;
}

/** The pop-up headline for a request waiting on me. */
export function requestPopupTitle(request) {
    const name = request?.from_name || 'A colleague';
    const client = request?.client_name || 'a client';
    if (request?.kind === 'join_request') return `${name} wants to join your conversation with ${client}`;
    if (request?.kind === 'pass') return `${name} wants to pass you the conversation with ${client}`;
    return `${name} invites you to join the conversation with ${client}`;
}

/** What the person who asked sees while waiting. There is no timeout: it stays until answered or withdrawn. */
export function waitingText(request, names = []) {
    const who = (names.filter(Boolean)[0]) || request?.to_name || 'They';
    return `${who} hasn't responded yet`;
}

export function privacyBlurb(level) {
    return PRIVACY_LEVELS.find((item) => item.value === normalizePrivacyLevel(level))?.help || '';
}

function plain(rows) {
    return Array.isArray(rows) ? rows : [];
}

/** Everything the inboxes need, in three independent reads: a failure in one never blanks the others. */
export async function loadConversationAccess(client, userId) {
    const result = { mineIds: new Set(), mineRows: [], cards: [], requests: [], available: true, error: '' };
    if (!client || !userId) return result;
    const safe = async (run) => {
        try { return await run(); } catch (failure) { return { data: null, error: failure }; }
    };
    const [mine, cards, requests] = await Promise.all([
        safe(() => client.from('inbox_conversation_members').select('session_id, user_id, added_by, added_via, joined_at').eq('user_id', userId)),
        safe(() => client.rpc('conv_colleague_cards')),
        safe(() => client.rpc('conv_my_access_requests')),
    ]);
    // Before the Step 2l migration is applied these do not exist: everything is simply the Company inbox.
    if (mine.error && cards.error && requests.error) {
        result.available = false;
        result.error = String(mine.error?.message || '');
        return result;
    }
    result.mineRows = plain(mine.data);
    result.mineIds = new Set(result.mineRows.map((row) => row.session_id));
    result.cards = plain(cards.data);
    result.requests = plain(requests.data);
    return result;
}

async function call(client, name, args) {
    const { data, error } = await client.rpc(name, args);
    if (error) throw new Error(error.message || 'That did not work. Please try again.');
    return data;
}

export async function takeConversation(client, { sessionId, userId, orgId }) {
    const { error } = await client.rpc('bl177_take_conversation', { p_session: sessionId });
    if (error) {
        const taken = /row-level security|duplicate key|unique/i.test(String(error.message || ''));
        throw new Error(taken ? 'Someone else just took this conversation.' : (error.message || 'Could not take this conversation.'));
    }
}

export async function leaveConversation(client, { sessionId, userId }) {
    const { error } = await client.from('inbox_conversation_members').delete().eq('session_id', sessionId).eq('user_id', userId);
    if (error) throw new Error(error.message || 'Could not leave this conversation.');
}

export const requestToJoin = (client, { sessionId, note }) => call(client, 'conv_request_join', { p_session: sessionId, p_note: note });
export const inviteColleague = (client, { sessionId, toUserId, note }) => call(client, 'conv_invite', { p_session: sessionId, p_to: toUserId, p_note: note, p_pass: false });
export const passTo = (client, { sessionId, toUserId, note }) => call(client, 'conv_invite', { p_session: sessionId, p_to: toUserId, p_note: note, p_pass: true });
export const decideRequest = (client, { requestId, allow }) => call(client, 'conv_decide_request', { p_request: requestId, p_allow: Boolean(allow) });
export const withdrawRequest = (client, { requestId }) => call(client, 'conv_withdraw_request', { p_request: requestId });
export async function listParticipants(client, sessionId) {
    return plain(await call(client, 'conv_participant_list', { p_session: sessionId }));
}
export async function listInvitable(client, sessionId) {
    return plain(await call(client, 'conv_invitable_staff', { p_session: sessionId }));
}

export async function loadMyPrivacy(client, userId) {
    if (!client || !userId) return DEFAULT_PRIVACY_LEVEL;
    try {
        const { data } = await client.from('user_conversation_privacy').select('level').eq('user_id', userId).maybeSingle();
        return normalizePrivacyLevel(data?.level);
    } catch {
        return DEFAULT_PRIVACY_LEVEL;
    }
}

export async function saveMyPrivacy(client, { userId, orgId, level }) {
    const { error } = await client.from('user_conversation_privacy')
        .upsert({ user_id: userId, org_id: orgId, level: normalizePrivacyLevel(level) }, { onConflict: 'user_id,org_id' });
    if (error) throw new Error(error.message || 'Could not save your privacy setting.');
}

// ---------------------------------------------------------------------------------------------
// BL-177 Step 2j (B1): group controls. A conversation is "just you", "invite sent" (waiting on a colleague) or a "group".
// The HOST is whoever took it, or was passed it: the earliest-joined person who came in by "take" or "pass" (an invited or allowed
// colleague is never the host). The database applies the same rule in conv_host_of(); only the host may close the group.
// ---------------------------------------------------------------------------------------------

/** The user id of the host among the participant rows (`conv_participant_list`), or null. */
export function hostOf(people = []) {
    const hosts = plain(people)
        .filter((person) => person && (person.added_via === 'take' || person.added_via === 'pass'))
        .sort((left, right) => (Date.parse(left.joined_at || '') || 0) - (Date.parse(right.joined_at || '') || 0) || String(left.user_id).localeCompare(String(right.user_id)));
    return hosts[0]?.user_id || null;
}

/** True when the signed-in person (the row flagged is_me) is the host. */
export function isHostOf(people = []) {
    const host = hostOf(people);
    return Boolean(host) && plain(people).some((person) => person.is_me && person.user_id === host);
}

/** The pending invite / pass I sent for one conversation, from the rows of `conv_my_access_requests`. */
export function pendingInviteFor(requests = [], sessionId) {
    return plain(requests).find((request) => request.session_id === sessionId && request.direction === 'outgoing'
        && request.status === 'pending' && (request.kind === 'invite' || request.kind === 'pass')) || null;
}

/** 'group' (more than one person in it), 'invite_pending' (just me, waiting on a colleague), or 'alone'. */
export function groupState(people = [], pendingInvite = null) {
    if (plain(people).length > 1) return 'group';
    return pendingInvite ? 'invite_pending' : 'alone';
}

/** What the invited colleague will see, in plain words (shown to the person who sent the invite). */
export function inviteSeenByText(request) {
    const kind = request?.kind === 'pass' ? 'pass' : 'invite';
    return kind === 'pass'
        ? 'They get a pop-up with your note and Allow / Decline. If they allow it, the conversation becomes theirs and you step out.'
        : 'They get a pop-up with your note and Allow / Decline. If they allow it, they see the whole conversation, including earlier messages.';
}

/** Host only: removes everyone but the host and returns the conversation to one-on-one. The thread stays open. */
export const closeGroupConversation = (client, { sessionId }) => call(client, 'conv_close_group', { p_session: sessionId });

/** The short alert for a person the host removed. */
export function removedAlertText(detail = {}) {
    const host = String(detail?.host || '').trim() || 'The host';
    const client = String(detail?.client || '').trim();
    return `${host} closed the group conversation${client ? ` with ${client}` : ''}. You are no longer in it.`;
}
